import React, {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import View, {
  ConfirmationView,
  DescriptionView,
  ImageUpload,
  OriginAddressSelection,
  PaymentMethod,
  PriceView,
  Resume,
} from './DigitalTwin.view'
import {
  Continuable,
  DigitalTwinProps,
  DraftShape,
  LocalReferenceableUpload,
  DigitalTwinSteps as Steps,
} from './DigitalTwin.types'
import { DigitalTwinTestIds } from './DigitalTwin.e2e'
import { useMarketplace } from 'context/Marketplace'
import {
  useSantitizedPrice,
  useUploadProgress,
} from 'features/CVM88/Form/Form.hook'
import { useAddresses, useUser } from 'context/User'
import { useMarketplaceDigitalTwinStorage } from './DigitalTwin.hooks'
import { useWebviewIntegration } from 'context/WebviewIntegration'
import { useHistory } from 'react-router-dom'
import ROUTES from 'core/modules/router'
import useLocalStorage from '@onepercentio/one-ui/hooks/persistence/useLocalStorage'
import { UserAddress } from 'core/modules/firebase/service/user'
import { AssetType } from 'core/logic/asset/asset.types'
import { t } from 'translate/i18n'
import { useScrollWhenEffect } from 'containers/MainRouter/MainRouter.hook'
import { useGallery } from 'context/Gallery'
import BigNumber from 'bignumber.js'
import AsyncControl from 'components/AsyncControl'
import { checkPixInfo } from 'core/modules/firebase/service/payments'

const { validate } = require('pixkey')

export const CHARS_RANGE = [10, 400]
export const preffixDraft = (draftKey: string) => `draft-${draftKey}`
export const draftId = (asset: Pick<AssetType, 'id' | 'tokenId'>) =>
  preffixDraft(`${asset.tokenId}-${asset.id}`)

function OriginAddressWrapper(
  p: Pick<
    ComponentProps<typeof OriginAddressSelection>,
    'selectedAddress' | 'onSelectOriginAddress' | 'onCancel' | 'draft'
  > &
    Continuable
) {
  const { addresses: availableAddresses, refreshAddresses } = useAddresses()
  return (
    <OriginAddressSelection
      {...p}
      mode='creation'
      onReloadAddresses={refreshAddresses}
      availableAddresses={availableAddresses}
    />
  )
}

export default function DigitalTwinLogic(props: DigitalTwinProps) {
  const { asset } = props
  const associatedGallery = useGallery(asset.galleryId!)
  const { detail } = useUser()
  const [savedDraft, setSavedDraft] = useLocalStorage<DraftShape>(
    draftId(props.asset),
    {}
  )

  const history = useHistory()
  const fileUpload = useRef<HTMLInputElement>(null)
  const [uploads, setUploads] = useState<LocalReferenceableUpload[]>([])
  const [description, setDescription] = useState<string>('')
  const [price, setPrice] = useState<string>('')
  const [paymentData, setPaymentData] = useState<string>('')
  const [originAddress, setOriginAddress] = useState<UserAddress>()
  const sanitizedPrice = useSantitizedPrice(price)
  const [currentStep, setCurrentStep] = useState<Steps>(Steps.PHOTOS)
  const { tax } = useMarketplace()
  const { initialImages, removeImage, uploadImage } =
    useMarketplaceDigitalTwinStorage(
      props.asset.galleryId!,
      props.asset.tokenId!,
      detail?.uid!
    )
  const { isIntegrationMode, openTermsAndCondition, on, exitWebview } =
    useWebviewIntegration()

  const [onExitAction, setOnExitAction] = useState<() => void>()

  const onSaveDraft = useCallback(() => {
    setSavedDraft({
      description,
      paymentData,
      originAddress: originAddress?.id,
      price,
    })
  }, [description, paymentData, originAddress, price])

  on(
    'onBack',
    () =>
      currentStep === Steps.PHOTOS
        ? setOnExitAction(() => () => {
            history.goBack()
          })
        : setCurrentStep((p) => p - 1),
    [currentStep]
  )
  on('onExit', () => setOnExitAction(() => () => exitWebview()), [currentStep])

  useEffect(() => {
    if (!!initialImages) setUploads((prev) => [...initialImages, ...prev])
  }, [initialImages])

  const uploadStatuses = new Array(4).fill(undefined).map((_, i) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useUploadProgress(uploads[i]?.task)
  })
  const uploadProgresses = uploadStatuses.map((a) => a.progress)

  useScrollWhenEffect(currentStep)

  const priceBounds = useMemo(() => {
    if (!associatedGallery) return
    const [lower, upper] = associatedGallery?.marketplace?.limitsRatio!
    const referencePrice = BigNumber(asset.referencePrice!).div(100).toNumber()
    return [
      referencePrice,
      BigNumber(referencePrice).multipliedBy(lower).toNumber(),
      BigNumber(referencePrice).multipliedBy(upper).toNumber(),
    ] as [number, number, number]
  }, [associatedGallery])

  function uploadFiles(files: FileList) {
    const uploadTasks = Array.from(files!).map((file) => uploadImage(file))
    setUploads((prev) => [
      ...prev,
      ...uploadTasks.map((task, idx) => ({
        task: task,
        objUrl: URL.createObjectURL(files![idx]),
        file: files![idx],
      })),
    ])
  }

  const continuable = useMemo(() => {
    switch (currentStep) {
      case Steps.PHOTOS:
        return (
          uploads.length > 0 &&
          !uploads.some((_, i) => uploadProgresses[i] !== 100)
        )
      case Steps.DESCRIPTION:
        return (
          description.length >= CHARS_RANGE[0] &&
          description.length <= CHARS_RANGE[1]
        )
      case Steps.RESUME:
        return true
      case Steps.PAYMENT_METHOD:
        return !!validate(paymentData)[0]
      case Steps.ORIGIN_ADDRESS:
        return !!originAddress
      case Steps.PRICE:
        return (
          sanitizedPrice > 0 &&
          priceBounds![1] <= sanitizedPrice &&
          priceBounds![2] >= sanitizedPrice
        )
    }
  }, [
    uploads,
    currentStep,
    sanitizedPrice,
    paymentData,
    originAddress,
    description,
    ...uploadProgresses,
  ])

  const continuableProps = {
    onContinue: () => {
      setCurrentStep((prev) => {
        if (prev === Steps.RESUME) {
          props.onSubmit({
            photos: uploads.map((u) =>
              'snapshot' in u.task
                ? u.task.snapshot.ref.fullPath
                : u.task.fullPath
            ),
            description,
            price: sanitizedPrice,
            address: originAddress!,
            paymentData,
          })
          return prev
        } else return prev + 1
      })
    },
    canContinue: !!continuable,
  }

  const content = (() => {
    switch (currentStep) {
      case Steps.PHOTOS:
        return (
          <ImageUpload
            retryUpload={() => {
              const uploadsToRemove = uploadStatuses
                .map((status, i) => {
                  if (status.error) return uploads[i]
                })
                .filter(Boolean)
              setUploads((prev) =>
                prev.filter((_) => !uploadsToRemove.includes(_))
              )
              uploadFiles(
                uploadsToRemove.map((u) => {
                  return u!.file
                }) as unknown as FileList
              )
            }}
            uploads={uploads}
            onUploadImage={() => fileUpload.current!.click()}
            asset={props.asset}
            onRemoveImage={(idx) => {
              const imageToRemove = uploads[idx]
              removeImage(imageToRemove).then(() => {
                setUploads((prev) =>
                  prev.filter((_, idxPrev) => idx !== idxPrev)
                )
              })
            }}
            {...continuableProps}
          />
        )
      case Steps.DESCRIPTION:
        return (
          <DescriptionView
            description={description}
            onChange={setDescription}
            draft={savedDraft}
            {...continuableProps}
          />
        )
      case Steps.PRICE:
        return (
          <PriceView
            price={price}
            setPrice={setPrice}
            tax={tax}
            draft={savedDraft}
            value={priceBounds!}
            {...continuableProps}
          />
        )
      case Steps.PAYMENT_METHOD:
        return (
          <PaymentMethod
            onChangePaymentData={setPaymentData}
            paymentData={paymentData}
            draft={savedDraft}
            {...continuableProps}
            onContinue={() =>
              checkPixInfo(paymentData).then(() => {
                continuableProps.onContinue()
              })
            }
          />
        )
      case Steps.ORIGIN_ADDRESS:
        return (
          <OriginAddressSelectionWrapper
            onSelectOriginAddress={setOriginAddress}
            selectedAddress={originAddress}
            draft={savedDraft}
            onCancel={() => setCurrentStep((e) => e - 1)}
            mode='creation'
            {...continuableProps}
          />
        )
      case Steps.RESUME:
        return (
          <Resume
            asset={props.asset}
            description={description}
            paymentData={paymentData}
            price={[price, tax!]}
            uploadedImages={uploads}
            onTerms={() => {
              if (isIntegrationMode) openTermsAndCondition()
              else window.open(ROUTES.unauthenticated.terms, '_blank')
            }}
            {...continuableProps}
          />
        )
    }
  })()

  return (
    <>
      <View {...props} currentStep={currentStep}>
        {content}
      </View>
      <input
        data-testid={DigitalTwinTestIds.FILE_UPLOAD}
        style={{ width: 1, height: 1, position: 'absolute' }}
        type='file'
        ref={fileUpload}
        accept='image/*'
        multiple
        onChange={async ({ target: { files } }) => {
          const maxFiles = 4 - uploads.length
          if (files!.length > maxFiles) {
            return alert(
              t('marketplace.offerCreation.sections.photos.alert', {
                count: maxFiles,
              })
            )
          }
          uploadFiles(files!)
        }}
      />
      <ConfirmationView
        open={!!onExitAction}
        onSaveWithoutDraft={() => {
          setSavedDraft({})
          Promise.all(uploads.map((upload) => removeImage(upload))).then(() =>
            setUploads([])
          )
          onExitAction!()
        }}
        onSaveWithDraft={() => {
          onSaveDraft()
          onExitAction!()
        }}
        onCancel={() => setOnExitAction(undefined)}
      />
    </>
  )
}

export function OriginAddressSelectionWrapper(
  props: Omit<
    ComponentProps<typeof OriginAddressSelection>,
    'availableAddresses' | 'onReloadAddresses'
  >
) {
  const { addresses, refreshAddresses, ...control } = useAddresses()
  return (
    <AsyncControl
      control={control}
      onClose={props.onCancel}
      onRetry={refreshAddresses}>
      {addresses && (
        <OriginAddressSelection
          availableAddresses={addresses!}
          onReloadAddresses={refreshAddresses}
          {...props}
        />
      )}
    </AsyncControl>
  )
}
